import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useBreadcrumb } from '../../../contexts/BreadcrumbContext';
import userLogService from '../../../services/userLogService';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faCircle,
    faChevronDown
} from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-toastify';
import { Badge } from 'react-bootstrap';
import Table from '../../../components/Common/Table/Table';
import Heading from '../../../components/Common/Heading/Heading';
import ListScreenSearchBar from '../../../components/Common/ListScreenSearchBar/ListScreenSearchBar';
import ListScreenPaginationBar from '../../../components/Common/ListScreenPaginationBar/ListScreenPaginationBar';
import AppDateInput from '../../../components/Common/AppDateInput/AppDateInput';
import '../../../components/Common/ListScreenPageLayout/listScreenPage.css';
import './UserLogs.css';

const parseLogDate = (value) => {
    if (value == null || value === '') return null;
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? null : d;
};

const formatDurationHMS = (ms) => {
    let n = typeof ms === 'number' ? ms : 0;
    if (n < 0) n = 0;
    const totalSec = Math.floor(n / 1000);
    const hours = Math.floor(totalSec / 3600);
    const mins = Math.floor((totalSec % 3600) / 60);
    const secs = totalSec % 60;
    return `${String(hours).padStart(2, '0')}:${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
};

const CHICAGO_IANA = 'America/Chicago';

const CST_DATE_FORMAT = new Intl.DateTimeFormat('en-US', {
    timeZone: CHICAGO_IANA,
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
});

const CST_TIME_FORMAT = new Intl.DateTimeFormat('en-US', {
    timeZone: CHICAGO_IANA,
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: true,
});

const CST_DATETIME_FORMAT = new Intl.DateTimeFormat('en-US', {
    timeZone: CHICAGO_IANA,
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: true,
});

/** YYYY-MM-DD in Chicago, for comparing clock-in vs clock-out calendar days. */
const formatCstDateKey = (value) => {
    const d = parseLogDate(value);
    if (!d) return '';
    return d.toLocaleDateString('en-CA', { timeZone: CHICAGO_IANA });
};

const formatCstDate = (value) => {
    const d = parseLogDate(value);
    if (!d) return 'N/A';
    return CST_DATE_FORMAT.format(d);
};

const formatCstTime = (value) => {
    const d = parseLogDate(value);
    if (!d) return 'N/A';
    return CST_TIME_FORMAT.format(d);
};

const formatCstDateTime = (value) => {
    const d = parseLogDate(value);
    if (!d) return 'N/A';
    return CST_DATETIME_FORMAT.format(d);
};

const MS_PER_DAY = 86400000;

/** When clock-out is earlier than clock-in on the same stored calendar day, assume next day(s) until span is positive. */
const sessionSpanMsClosedSession = (clockIn, clockOut) => {
    if (!clockIn || !clockOut) return 0;
    const start = clockIn.getTime();
    let end = clockOut.getTime();
    if (Number.isNaN(start) || Number.isNaN(end)) return 0;
    let guard = 0;
    while (end <= start && guard < 14) {
        end += MS_PER_DAY;
        guard += 1;
    }
    return Math.max(0, end - start);
};

const formatSessionDurationFromLog = (log) => {
    const clockIn = parseLogDate(log?.clock_in);
    if (!clockIn) return 'N/A';
    const clockOut = parseLogDate(log?.clock_out);
    if (clockOut) {
        return formatDurationHMS(sessionSpanMsClosedSession(clockIn, clockOut));
    }
    const updatedAt = parseLogDate(log?.updated_at);
    const end = updatedAt && updatedAt >= clockIn ? updatedAt : new Date();
    return formatDurationHMS(Math.max(0, end.getTime() - clockIn.getTime()));
};

const localTodayIso = () => {
    const d = new Date();
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
};

const UserLogs = ({ embedded = false } = {}) => {
    const { updateBreadcrumbs, clearBreadcrumbs } = useBreadcrumb();
    const todayIso = useMemo(() => localTodayIso(), []);

    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalLogs, setTotalLogs] = useState(0);
    const [search, setSearch] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState(todayIso);
    const [appliedStartDate, setAppliedStartDate] = useState('');
    const [appliedEndDate, setAppliedEndDate] = useState(todayIso);
    const [queryVersion, setQueryVersion] = useState(0);
    const [entriesPerPage, setEntriesPerPage] = useState('10');
    const [sortBy, setSortBy] = useState('clock_in');
    const [sortOrder, setSortOrder] = useState('desc');
    const [showExportMenu, setShowExportMenu] = useState(false);
    const exportMenuRef = useRef(null);

    const limit = useMemo(
        () => (entriesPerPage === 'all' ? 9999 : parseInt(entriesPerPage, 10) || 10),
        [entriesPerPage]
    );
    const entriesSelectValue = entriesPerPage === 'all' ? 'all' : String(entriesPerPage);
    const startDateIso = startDate || '';
    const endDateIso = endDate || '';

    useEffect(() => {
        if (embedded) {
            updateBreadcrumbs([
                { label: 'Settings', path: '/settings' },
                { label: 'User Logs', path: '/settings?tab=user-logs', active: true }
            ]);
        } else {
            updateBreadcrumbs([
                { label: 'User Logs', path: null, active: true }
            ]);
        }
        return () => clearBreadcrumbs();
    }, [updateBreadcrumbs, clearBreadcrumbs, embedded]);

    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearch(search);
            setPage(1);
        }, 500);
        return () => clearTimeout(timer);
    }, [search]);

    useEffect(() => {
        if (!showExportMenu) return undefined;
        const handleClickOutside = (event) => {
            if (exportMenuRef.current && !exportMenuRef.current.contains(event.target)) {
                setShowExportMenu(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [showExportMenu]);

    const fetchLogs = useCallback(async () => {
        setLoading(true);
        try {
            const filters = {
                page,
                limit,
                search: debouncedSearch,
                startDate: appliedStartDate || '',
                endDate: appliedEndDate || '',
                sortBy,
                sortOrder
            };
            const response = await userLogService.getUserLogs(filters);
            if (response.success) {
                setLogs(response.logs);
                setTotalPages(response.pagination.total_pages);
                setTotalLogs(response.pagination.total);
            } else {
                toast.error(response.message || 'Failed to fetch user logs');
            }
        } catch (error) {
            console.error('Failed to fetch user logs:', error);
            toast.error(error.response?.data?.message || 'Failed to fetch user logs');
        } finally {
            setLoading(false);
        }
    }, [page, limit, debouncedSearch, appliedStartDate, appliedEndDate, sortBy, sortOrder]);

    useEffect(() => {
        fetchLogs();
    }, [fetchLogs, queryVersion]);

    useEffect(() => {
        if (totalPages > 0 && page > totalPages) {
            setPage(totalPages);
        }
    }, [totalPages, page]);

    const handleQuery = () => {
        setAppliedStartDate(startDate || '');
        setAppliedEndDate(endDate || '');
        setPage(1);
        setQueryVersion((v) => v + 1);
    };

    const handleReset = () => {
        setSearch('');
        setDebouncedSearch('');
        setStartDate('');
        setEndDate(todayIso);
        setAppliedStartDate('');
        setAppliedEndDate(todayIso);
        setPage(1);
        setQueryVersion((v) => v + 1);
    };

    const handleExport = async () => {
        setShowExportMenu(false);
        try {
            const filters = {
                search: debouncedSearch,
                startDate: appliedStartDate || '',
                endDate: appliedEndDate || '',
                sortBy,
                sortOrder
            };
            const blob = await userLogService.exportUserLogsCsv(filters);
            const url = window.URL.createObjectURL(new Blob([blob], { type: 'text/csv' }));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `user-logs-${localTodayIso()}.csv`);
            document.body.appendChild(link);
            link.click();
            link.parentNode.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Export failed:', error);
            toast.error(error.response?.data?.message || 'Failed to export user logs');
        }
    };

    const getStatusBadge = (status) => {
        const dot = (extraClass = '') => (
            <FontAwesomeIcon
                icon={faCircle}
                className={['status-pill-dot', extraClass].filter(Boolean).join(' ')}
            />
        );
        switch (status) {
            case 'Logged in':
            case 'Active':
                return (
                    <span className="status-pill status-pill--active">
                        {dot('status-pill-dot--pulse')} Logged in
                    </span>
                );
            case 'Session timed out':
            case 'Not Active':
                return (
                    <span className="status-pill status-pill--inactive">
                        {dot()} Session timed out
                    </span>
                );
            case 'Logged out':
            case 'User logged out':
                return (
                    <span className="status-pill status-pill--logged-out">
                        {dot()} Logged out
                    </span>
                );
            default:
                return (
                    <span className="status-pill status-pill--default">
                        {dot()} {status}
                    </span>
                );
        }
    };

    const handleSort = (columnKey) => {
        if (!columnKey) return;
        if (sortBy === columnKey) {
            setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
        } else {
            setSortBy(columnKey);
            setSortOrder('asc');
        }
        setPage(1);
    };

    const handleStartDateChange = (value) => {
        const next = value || '';
        setStartDate(next);
        if (next && endDate && endDate < next) {
            setEndDate(next);
        }
    };

    const handleEndDateChange = (value) => {
        if (!value) {
            setEndDate('');
            return;
        }
        const minDate = startDate || '';
        let normalized = value;
        if (minDate && normalized < minDate) normalized = minDate;
        setEndDate(normalized);
    };

    const displayLogIdForRow = (rowIndex) => {
        const currentOffset = (page - 1) * limit;
        if (sortOrder === 'asc') {
            return currentOffset + rowIndex + 1;
        }
        // Default current UX is newest first (desc): keep numbering continuous downward.
        return Math.max(totalLogs - currentOffset - rowIndex, 1);
    };

    const columns = [
        {
            title: 'Log ID',
            key: 'log_id',
            sortable: true,
            render: (log, rowIndex) => (
                <span className="fw-bold text-muted" title={`Activity ID: ${log.log_id}`}>
                    {displayLogIdForRow(rowIndex)}
                </span>
            )
        },
        { title: 'User Type', key: 'user_type', sortable: true, render: (log) => <Badge className="user-type-badge">{log.user_type}</Badge> },
        { title: 'User Name', key: 'username', sortable: true, render: (log) => <span className="fw-semibold username-text">{log.username}</span> },
        { title: 'Name', key: 'name', sortable: true },
        { title: 'Date', key: 'date', sortable: true, render: (log) => formatCstDate(log.clock_in) },
        { 
            title: 'Clock In (CST)', 
            key: 'clock_in', 
            sortable: true,
            className: 'time-cell', 
            render: (log) => formatCstTime(log.clock_in)
        },
        { 
            title: 'Clock Out (CST)', 
            key: 'clock_out', 
            sortable: true,
            className: 'time-cell', 
            render: (log) => {
                if (!log.clock_out) return '---';
                const inDay = formatCstDateKey(log.clock_in);
                const outDay = formatCstDateKey(log.clock_out);
                if (inDay && outDay && inDay !== outDay) {
                    return formatCstDateTime(log.clock_out);
                }
                return formatCstTime(log.clock_out);
            } 
        },
        {
            title: 'Session Duration',
            key: 'duration',
            sortable: true,
            className: 'duration-cell time-cell',
            render: (log) => formatSessionDurationFromLog(log)
        },
        { title: 'IP Address', key: 'ip_address', sortable: true, className: 'ip-cell text-muted' },
        {
            title: 'Status',
            key: 'status',
            sortable: true,
            className: 'user-logs-col-status reusable-table-centered-col',
            render: (log) => getStatusBadge(log.status)
        }
    ];

    return (
        <div className="user-logs-page list-screen-page-container">
            <div className="user-logs-header-flex list-screen-top-bar">
                <div className="list-screen-title-section">
                    {!embedded && (
                        <Heading title="User Logs" level="h2" className="user-logs-title list-screen-title-text" />
                    )}
                </div>
                <div className="list-screen-actions">
                    <ListScreenSearchBar
                        value={search}
                        onChange={setSearch}
                        inputId="user-logs-search"
                    />
                </div>
            </div>

            <div className="user-logs-filters">
                <div className="user-logs-filter-row">
                    <div className="user-logs-date-field">
                        <label className="user-logs-date-label" htmlFor="user-logs-start-date">Start Date</label>
                        <AppDateInput
                            id="user-logs-start-date"
                            value={startDateIso}
                            onChange={handleStartDateChange}
                            max={endDateIso || undefined}
                            className="form-control user-logs-date-input"
                            showCalendarIcon
                            placeholder="mm/dd/yyyy"
                        />
                    </div>
                    <div className="user-logs-date-field">
                        <label className="user-logs-date-label" htmlFor="user-logs-end-date">End Date</label>
                        <AppDateInput
                            id="user-logs-end-date"
                            value={endDateIso}
                            onChange={handleEndDateChange}
                            min={startDateIso || ''}
                            className="form-control user-logs-date-input"
                            showCalendarIcon
                            placeholder="mm/dd/yyyy"
                        />
                    </div>
                    <div className="user-logs-action-btns">
                        <button type="button" className="user-logs-action-btn" onClick={handleReset} disabled={loading}>
                            Reset
                        </button>
                        <button type="button" className="user-logs-action-btn" onClick={handleQuery} disabled={loading}>
                            Query
                        </button>
                        <div className="user-logs-export-wrap" ref={exportMenuRef}>
                            <button
                                type="button"
                                className="user-logs-action-btn"
                                onClick={() => setShowExportMenu((prev) => !prev)}
                                disabled={loading || totalLogs === 0}
                            >
                                Export <FontAwesomeIcon icon={faChevronDown} className="user-logs-export-caret" />
                            </button>
                            {showExportMenu && (
                                <div className="user-logs-export-menu">
                                    <button type="button" onClick={handleExport}>CSV</button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            <div className="user-logs-table-container">
                <Table
                    columns={columns}
                    data={logs}
                    sortBy={sortBy}
                    sortOrder={sortOrder}
                    onSort={handleSort}
                    isLoading={loading}
                    emptyMessage="No session logs found matching the criteria."
                    loadingMode="spinner"
                />
            </div>

            {totalLogs > 0 && (
                <div className="user-logs-pagination">
                    <ListScreenPaginationBar
                        entriesValue={entriesSelectValue}
                        onEntriesChange={(value) => {
                            setEntriesPerPage(value);
                            setPage(1);
                        }}
                        currentPage={page}
                        totalPages={totalPages}
                        onPageChange={setPage}
                    />
                </div>
            )}
        </div>
    );
};

export default UserLogs;
